/**
 * LCOE-EXPLAIN-1 (2026-07-03) - shared derivation + composer for the LCOE
 * Cost Comparison exhibit write-up.
 *
 * William: "LCOE Cost Comparison has no write-up of explanation?" - every
 * surface that renders the exhibit (view/PDF chart section, DOCX, PPTX) now
 * carries the SAME deterministic analysis paragraph, composed ONLY from the
 * chart's own values (corrected LCOE bands, published industry-norm
 * midpoints, the grid retail reference line). No generated text, nothing
 * invented - this module is the single source for that text so the three
 * surfaces can never drift apart.
 *
 * Derivation mirrors LcoeChartSection's row build (corrected → ±BAND_PCT
 * band; else industry-norm baseline flagged as benchmark; else pending) -
 * the component consumes THIS module for its explainer so text parity is
 * structural, not coincidental.
 */

import type { LcoeBasis } from '../lcoeBasis';
import { normalizeElectricityPriceToCents } from '../reportFormatters';
import {
  resolveReportCorrectedLcoe,
  correctedLcoeForBucket,
  type LcoeTechBucket,
} from '../finDecision/reportLcoeSurface';
import { INDUSTRY_BASELINES, type BaselineKey } from '../../services/financial/industryBaselines';
import type { SiteRagReport } from '../../types/siteRag';

export interface LcoeExplainerRow {
  tech: string;
  low: number;
  high: number;
  spacer?: number;
  range?: number;
  note?: string;
  paletteIdx?: number;
  mid?: number | null;
  scenarioCount?: number;
  pending: boolean;
  industryNorm: boolean;
  basis?: LcoeBasis;
  currencyYear?: number | string | null;
  currencyBasis?: 'real' | 'nominal' | null;
  reportedCurrencyBases?: readonly ('real' | 'nominal')[];
  subsidyStatus?: 'unsubsidized' | 'subsidized' | null;
  subsidySummary?: string;
  firmingBasis?: '24/7-load-sized' | 'plant-level-model' | null;
  serviceScope?: 'served-load' | 'busbar' | null;
  maturityScenario?: 'FOAK' | 'NOAK' | 'FOAK-and-NOAK' | 'unspecified';
  referenceRangeText?: string;
  referenceSourceUrl?: string;
}

export type LcoeExhibitRow = LcoeExplainerRow;

export interface LcoeGridComparisonBasis {
  tariffBasis?:
    | 'verified-all-in-site-tariff'
    | 'state-industrial-average'
    | 'regional-retail-benchmark'
    | 'unverified'
    | null;
  currencyYear?: number | string | null;
  currencyBasis?: 'real' | 'nominal' | null;
  subsidyStatus?: 'unsubsidized' | 'subsidized' | null;
  firmingBasis?: '24/7-load-sized' | 'plant-level-model' | null;
  serviceScope?: 'served-load' | 'busbar' | null;
}

export interface LcoePathwayComparabilityAssessment {
  canRankPathways: boolean;
  hasMissingBasis: boolean;
  missingDimensions: string[];
  mismatchedDimensions: string[];
  incompatibilityExplanation: string | null;
}

function normalizeYearString(val: number | string | null | undefined): string {
  if (val == null) return '';
  return String(val).trim();
}

export function rowHasCompleteBasis(r: LcoeExplainerRow): boolean {
  return (
    r.basis !== 'benchmark-pathway'
    && normalizeYearString(r.currencyYear) !== ''
    && (r.currencyBasis === 'real' || r.currencyBasis === 'nominal')
    && (r.subsidyStatus === 'unsubsidized' || r.subsidyStatus === 'subsidized')
    && (r.firmingBasis === '24/7-load-sized' || r.firmingBasis === 'plant-level-model')
    && (r.serviceScope === 'served-load' || r.serviceScope === 'busbar')
  );
}

export function assessLcoePathwayComparability(
  corrected: readonly LcoeExplainerRow[],
): LcoePathwayComparabilityAssessment {
  const missingSet = new Set<string>();
  for (const r of corrected) {
    if (r.basis === 'benchmark-pathway') missingSet.add('verified cash-flow dollar basis');
    if (normalizeYearString(r.currencyYear) === '') missingSet.add('currency year');
    if (r.currencyBasis !== 'real' && r.currencyBasis !== 'nominal') {
      missingSet.add('real/nominal dollar basis');
    }
    if (r.subsidyStatus !== 'unsubsidized' && r.subsidyStatus !== 'subsidized') {
      missingSet.add('incentive/subsidy basis');
    }
    if (r.firmingBasis !== '24/7-load-sized' && r.firmingBasis !== 'plant-level-model') {
      missingSet.add('time/firming basis');
    }
    if (r.serviceScope !== 'served-load' && r.serviceScope !== 'busbar') {
      missingSet.add('service scope');
    }
  }

  const mismatchedDimensions: string[] = [];
  if (corrected.length >= 2) {
    const years = new Set(corrected.map((r) => normalizeYearString(r.currencyYear)).filter(Boolean));
    if (years.size > 1) mismatchedDimensions.push('currency year');

    const currencyBases = new Set(corrected.map((r) => r.currencyBasis).filter(Boolean));
    if (currencyBases.size > 1) mismatchedDimensions.push('real vs. nominal dollar basis');

    const subsidyBases = new Set(
      corrected
        .map((r) =>
          r.subsidyStatus === 'subsidized'
            ? `subsidized:${(r.subsidySummary ?? '').trim()}`
            : r.subsidyStatus,
        )
        .filter(Boolean),
    );
    if (subsidyBases.size > 1) mismatchedDimensions.push('incentive/subsidy basis');

    const firmingBases = new Set(corrected.map((r) => r.firmingBasis).filter(Boolean));
    if (firmingBases.size > 1) {
      mismatchedDimensions.push('time/firming basis (24/7 load-sized vs. standalone plant)');
    }

    const serviceScopes = new Set(corrected.map((r) => r.serviceScope).filter(Boolean));
    if (serviceScopes.size > 1) {
      mismatchedDimensions.push('service scope (served-load vs. busbar)');
    }
  }

  const missingDimensions = [...missingSet];
  const hasMissingBasis = missingDimensions.length > 0;
  const canRankPathways =
    corrected.length >= 2 && !hasMissingBasis && mismatchedDimensions.length === 0;

  let incompatibilityExplanation: string | null = null;
  if (corrected.length > 0 && (hasMissingBasis || mismatchedDimensions.length > 0)) {
    if (mismatchedDimensions.length > 0 && hasMissingBasis) {
      incompatibilityExplanation = `pathways have incompatible ${mismatchedDimensions.join(', ')} and missing ${missingDimensions.join(', ')} metadata`;
    } else if (mismatchedDimensions.length > 0) {
      incompatibilityExplanation = `pathways differ in ${mismatchedDimensions.join(', ')}`;
    } else {
      incompatibilityExplanation = `common ${missingDimensions.join(', ')} metadata is not established`;
    }
  }

  return {
    canRankPathways,
    hasMissingBasis,
    missingDimensions,
    mismatchedDimensions,
    incompatibilityExplanation,
  };
}

export function canCompareRowToGrid(
  row: LcoeExplainerRow,
  finiteGridLine: number | null,
  gridBasis?: LcoeGridComparisonBasis | null,
): boolean {
  if (finiteGridLine == null || !gridBasis) return false;
  if (!rowHasCompleteBasis(row)) return false;
  if (gridBasis.tariffBasis !== 'verified-all-in-site-tariff') return false;
  if (normalizeYearString(gridBasis.currencyYear) !== normalizeYearString(row.currencyYear)) {
    return false;
  }
  if (gridBasis.currencyBasis !== row.currencyBasis) return false;
  if ((gridBasis.subsidyStatus ?? 'unsubsidized') !== row.subsidyStatus) return false;
  if (gridBasis.firmingBasis !== row.firmingBasis) return false;
  if (gridBasis.serviceScope !== row.serviceScope) return false;
  return true;
}

// MUST mirror LcoeChartSection's ROW_DEFS + BUCKET_BASELINE_KEY exactly.
const ROW_DEFS: Array<{ tech: string; bucket: LcoeTechBucket; baselineKey: BaselineKey }> = [
  { tech: 'Nuclear SMR', bucket: 'nuclear', baselineKey: 'smr-noak' },
  { tech: 'Natural Gas', bucket: 'gas', baselineKey: 'gas-cc' },
  { tech: 'Solar + BESS', bucket: 'solar', baselineKey: 'solar-bess' },
  { tech: 'Wind + BESS', bucket: 'wind', baselineKey: 'wind-bess' },
];

export function formatPublishedReferenceNoteForBucket(bucket: LcoeTechBucket): string {
  switch (bucket) {
    case 'nuclear': {
      const foak = INDUSTRY_BASELINES['smr-foak'].publishedReferenceLcoe!;
      const noak = INDUSTRY_BASELINES['smr-noak'].publishedReferenceLcoe!;
      return `catalog reference: $${foak.lowUsdPerMwh}–$${foak.highUsdPerMwh}/MWh 2023 FOAK · $${noak.lowUsdPerMwh}–$${noak.highUsdPerMwh}/MWh 2035 NOAK (reported labels: ${noak.currencyBasis}, ${noak.subsidyStatus}, EPRI 2024 3002029428; source-page and basis verification pending)`;
    }
    case 'gas': {
      const ref = INDUSTRY_BASELINES['gas-cc'].publishedReferenceLcoe!;
      return `catalog reference: $${ref.lowUsdPerMwh}–$${ref.highUsdPerMwh}/MWh (reported labels: ${ref.currencyBasis}, ${ref.subsidyStatus} busbar CCGT, Lazard v17.0 June 2024; source-page and basis verification pending)`;
    }
    case 'solar': {
      const ref = INDUSTRY_BASELINES['solar-bess'].publishedReferenceLcoe!;
      return `catalog reference: $${ref.lowUsdPerMwh}–$${ref.highUsdPerMwh}/MWh (reported labels: ${ref.currencyBasis}, ${ref.subsidyStatus} standalone PV + 4-hr storage, Lazard v17.0 June 2024; source-page and basis verification pending)`;
    }
    case 'wind': {
      const ref = INDUSTRY_BASELINES['wind-bess'].publishedReferenceLcoe!;
      return `catalog reference: $${ref.lowUsdPerMwh}–$${ref.highUsdPerMwh}/MWh (reported labels: ${ref.currencyBasis}, ${ref.subsidyStatus} standalone wind + storage, Lazard v17.0 June 2024; source-page and basis verification pending)`;
    }
    default:
      return 'pending site-specific model';
  }
}

/** Catalog labels and ranges are provenance, not completed primary-source verification. */
export const LCOE_REFERENCE_VERIFICATION_NOTE = 'Catalog-reported values and basis labels are retained for context; exact source tables/pages, dollar year, real/nominal basis, financing/tax assumptions, and service boundaries remain unverified. They are not site estimates or interchangeable 24/7 firmed-load costs.';

/** Shared by the export prose, chart footnote, and nearby row captions. */
export function composePublishedLcoeReferenceContext(): string {
  const epri = INDUSTRY_BASELINES['smr-noak'].publishedReferenceLcoe!;
  const lazard = INDUSTRY_BASELINES['gas-cc'].publishedReferenceLcoe!;
  // The full standalone row captions remain qualified. In this one paragraph,
  // factor their identical qualification and publication names once, before
  // the ranges and in the explicitly mapped source list respectively.
  const range = (bucket: LcoeTechBucket): string => formatPublishedReferenceNoteForBucket(bucket)
    .replace('catalog reference: ', '')
    .replace('reported labels: ', '')
    .replace('; source-page and basis verification pending', '')
    .replace(', EPRI 2024 3002029428', '')
    .replace(', Lazard v17.0 June 2024', '');
  return `${LCOE_REFERENCE_VERIFICATION_NOTE} Catalog-reported ranges: Nuclear SMR ${range('nuclear')}; Natural Gas ${range('gas')}; Solar + BESS ${range('solar')}; Wind + BESS ${range('wind')}. Sources: SMR — EPRI 2024 Report 3002029428 (${epri.sourceUrl}); gas/solar/wind — Lazard LCOE+ v17.0 (June 2024, ${lazard.sourceUrl}).`;
}

export function isLcoeUnavailableInFrozenSource(report: SiteRagReport): boolean {
  const corrected = resolveReportCorrectedLcoe(report);
  const hasSiteComputed = ROW_DEFS.some(
    ({ bucket }) => correctedLcoeForBucket(corrected, bucket).midUsdPerMwh != null,
  );
  if (hasSiteComputed) return false;

  const sourceCoverage = report.screeningBasis?.sourceCoverage;
  if (
    Array.isArray(sourceCoverage) &&
    sourceCoverage.some(
      (entry) =>
        entry != null &&
        typeof entry === 'object' &&
        (entry as { section?: string }).section === 'lcoe' &&
        (entry as { status?: string }).status === 'unavailable',
    )
  ) {
    return true;
  }

  const omissions = (
    report as unknown as {
      reportRevision?: {
        document?: { omissions?: Array<{ sectionId?: string; reason?: string }> };
      };
    }
  ).reportRevision?.document?.omissions;
  if (
    Array.isArray(omissions) &&
    omissions.some((o) => o != null && o.sectionId === 'lcoeChart')
  ) {
    return true;
  }

  return false;
}

export function deriveLcoeExplainerRows(report: SiteRagReport): {
  rows: LcoeExplainerRow[];
  gridLineUsdPerMwh: number | null;
  gridBasis: LcoeGridComparisonBasis | null;
} {
  const ds = report.datasets;
  const elec = ds?.electricity as {
    retailRate?: number;
    year?: number | string;
    basis?: string;
    currencyBasis?: 'real' | 'nominal';
    subsidyStatus?: 'unsubsidized' | 'subsidized';
    firmingBasis?: '24/7-load-sized' | 'plant-level-model';
    serviceScope?: 'served-load' | 'busbar';
  } | undefined;
  const gridRate = normalizeElectricityPriceToCents({
    retailRate: elec?.retailRate,
    basis: elec?.basis === 'state-industrial-average' ? 'state-industrial-average' : undefined,
  });
  const gridLineUsdPerMwh = gridRate != null ? Math.round(gridRate * 10 * 10) / 10 : null;
  const gridBasis: LcoeGridComparisonBasis | null =
    gridLineUsdPerMwh != null
      ? {
          tariffBasis:
            elec?.basis === 'verified-all-in-site-tariff'
              ? 'verified-all-in-site-tariff'
              : elec?.basis === 'state-industrial-average'
                ? 'state-industrial-average'
                : 'regional-retail-benchmark',
          currencyYear: elec?.year ?? null,
          currencyBasis: elec?.currencyBasis ?? null,
          subsidyStatus: elec?.subsidyStatus ?? null,
          firmingBasis: elec?.firmingBasis ?? null,
          serviceScope: elec?.serviceScope ?? null,
        }
      : null;

  if (isLcoeUnavailableInFrozenSource(report)) {
    return {
      rows: ROW_DEFS.map(({ tech }) => ({
        tech,
        low: 0,
        high: 0,
        pending: true,
        industryNorm: false,
      })),
      gridLineUsdPerMwh,
      gridBasis,
    };
  }

  const corrected = resolveReportCorrectedLcoe(report);
  const rows: LcoeExplainerRow[] = ROW_DEFS.map(({ tech, bucket, baselineKey }) => {
    const entry = correctedLcoeForBucket(corrected, bucket);
    const mid = entry.midUsdPerMwh;
    const pubRef = INDUSTRY_BASELINES[baselineKey].publishedReferenceLcoe;
    if (mid != null) {
      const low = Math.round(entry.lowUsdPerMwh ?? mid);
      const high = Math.round(entry.highUsdPerMwh ?? mid);
      return {
        tech,
        low,
        high,
        mid: Math.round(mid),
        scenarioCount: entry.scenarioCount ?? 1,
        pending: false,
        industryNorm: false,
        basis: entry.basis,
        currencyYear: entry.currencyYear,
        currencyBasis: entry.currencyBasis,
        reportedCurrencyBases: entry.reportedCurrencyBases,
        subsidyStatus: entry.subsidyStatus,
        subsidySummary: entry.subsidySummary,
        firmingBasis: entry.firmingBasis,
        serviceScope: entry.serviceScope,
        maturityScenario: entry.maturityScenario,
        referenceRangeText: formatPublishedReferenceNoteForBucket(bucket),
        referenceSourceUrl: pubRef?.sourceUrl,
      };
    }
    return {
      tech,
      low: 0,
      high: 0,
      mid: null,
      scenarioCount: 0,
      pending: true,
      industryNorm: false,
      referenceRangeText: formatPublishedReferenceNoteForBucket(bucket),
      referenceSourceUrl: pubRef?.sourceUrl,
    };
  });
  return { rows, gridLineUsdPerMwh, gridBasis };
}

/** A reported engine label is not proof of a consistent cash-flow dollar basis. */
export function describeLcoeDollarBasis(
  row: Pick<LcoeExplainerRow, 'basis' | 'currencyBasis' | 'reportedCurrencyBases'>,
): string {
  if (row.basis === 'benchmark-pathway') {
    const labels = row.reportedCurrencyBases ?? (row.currencyBasis ? [row.currencyBasis] : []);
    const provenance = labels.length ? ` (reported label${labels.length > 1 ? 's' : ''}: ${labels.join(', ')})` : '';
    return `cash-flow dollar basis unverified${provenance}`;
  }
  return row.currencyBasis ? `${row.currencyBasis} $/MWh` : 'dollar basis unknown';
}

export function composeLcoeMethodologyNote(rows: readonly LcoeExplainerRow[]): string {
  const available = rows.filter(row => !row.pending && !row.industryNorm);
  const notes: string[] = [];
  if (available.some(row => row.basis === 'benchmark-pathway')) {
    notes.push('Benchmark pathway figures retain reported values and labels; cash-flow dollar basis unverified, so ranking and tariff comparisons are pending.');
  }
  if (available.some(row => row.basis === 'project-finance')) {
    notes.push('Saved financial model figures use recorded model inputs; dollar and incentive bases are stated for each row.');
  }
  if (notes.length === 0) {
    notes.push('Site-specific LCOE is pending project financial assumptions; catalog-reported ranges are unverified reference context only.');
  }
  return notes.join(' ') + ' No artificial ±8% band is used. ';
}

export function composeLcoeExplainer(
  rows: readonly LcoeExplainerRow[],
  gridLineUsdPerMwh: number | null,
  gridBasis?: LcoeGridComparisonBasis | null,
): string {
  const finiteGridLine = gridLineUsdPerMwh != null && Number.isFinite(gridLineUsdPerMwh) && gridLineUsdPerMwh >= 0
    ? gridLineUsdPerMwh : null;
  const corrected = rows.filter((r) => !r.pending && !r.industryNorm);
  const pendingRows = rows.filter((r) => r.pending || r.industryNorm);
  const mid = (r: LcoeExplainerRow): number =>
    r.mid != null && Number.isFinite(r.mid) ? Math.round(r.mid) : Math.round((r.low + r.high) / 2);
  const money = (n: number): string => `$${Math.round(n)}/MWh`;
  const formatRowFigure = (r: LcoeExplainerRow): string => {
    const basisParts: string[] = [];
    if (r.basis === 'benchmark-pathway') basisParts.push('benchmark pathway model');
    if (r.basis === 'project-finance') basisParts.push('saved financial model');
    const yr = normalizeYearString(r.currencyYear);
    if (r.basis === 'benchmark-pathway') {
      basisParts.push(describeLcoeDollarBasis(r));
      if (yr) basisParts.push(`reported currency year: ${yr}`);
    } else if (yr && r.currencyBasis) {
      basisParts.push(`${yr}$ ${r.currencyBasis} $/MWh`);
    } else if (r.currencyBasis) {
      basisParts.push(describeLcoeDollarBasis(r));
    }
    if (r.subsidyStatus === 'subsidized') {
      basisParts.push(r.subsidySummary ? `subsidized: ${r.subsidySummary}` : 'subsidized');
    } else if (r.subsidyStatus === 'unsubsidized') {
      basisParts.push('unsubsidized');
    }
    if (r.maturityScenario && r.maturityScenario !== 'unspecified') {
      basisParts.push(r.maturityScenario);
    }
    if (r.firmingBasis === '24/7-load-sized') {
      basisParts.push('24/7 load-sized');
    } else if (r.firmingBasis === 'plant-level-model') {
      basisParts.push('user financial model');
    }
    if (r.serviceScope === 'served-load') {
      basisParts.push('served-load');
    } else if (r.serviceScope === 'busbar') {
      basisParts.push('busbar');
    }
    const qualifier = basisParts.length > 0 ? ` (${basisParts.join(', ')})` : '';
    if (r.low < r.high) {
      return `${money(r.low)}–${money(r.high)}${qualifier}`;
    }
    return `${money(mid(r))}${qualifier}`;
  };

  const parts: string[] = [];
  const comparability = assessLcoePathwayComparability(corrected);
  let comparedDirectlyToGrid = false;

  if (corrected.length > 0) {
    if (comparability.canRankPathways) {
      const ranked = [...corrected].sort((a, b) => mid(a) - mid(b));
      const best = ranked[0]!;
      const worst = ranked[ranked.length - 1]!;
      let lead = `Among the modeled options on a common basis, ${best.tech} carries the lowest levelized cost at ${formatRowFigure(best)}`;
      if (canCompareRowToGrid(best, finiteGridLine, gridBasis)) {
        const rel = mid(best) < finiteGridLine! ? 'below' : 'above';
        lead += `, ${rel} the verified all-in site tariff benchmark of ${money(finiteGridLine!)}`;
        comparedDirectlyToGrid = true;
      }
      parts.push(lead + '.');
      parts.push(
        `${worst.tech} is the highest-cost modeled pathway at ${formatRowFigure(worst)}, a spread of about ${money(mid(worst) - mid(best)).replace('/MWh', ' per MWh')} against ${best.tech}.`,
      );
    } else if (corrected.length === 1) {
      const single = corrected[0]!;
      if (canCompareRowToGrid(single, finiteGridLine, gridBasis)) {
        const rel = mid(single) < finiteGridLine! ? 'below' : 'above';
        parts.push(
          `The modeled levelized cost for ${single.tech} is ${formatRowFigure(single)}, ${rel} the verified all-in site tariff benchmark of ${money(finiteGridLine!)}.`,
        );
        comparedDirectlyToGrid = true;
      } else {
        const missingNote = comparability.incompatibilityExplanation
          ? ` (shown as a standalone figure without cross-option ranking because ${comparability.incompatibilityExplanation})`
          : '';
        parts.push(
          `The modeled levelized cost for ${single.tech} is ${formatRowFigure(single)}${missingNote}.`,
        );
      }
    } else {
      const separateList = corrected.map((r) => `${r.tech} at ${formatRowFigure(r)}`).join('; ');
      parts.push(
        `Modeled LCOE values are reported separately without ranking or spread calculation because ${comparability.incompatibilityExplanation ?? 'a common basis is not established'}: ${separateList}.`,
      );
    }
  }

  if (corrected.some((r) => r.basis === 'benchmark-pathway')) {
    parts.push('Benchmark pathway values use modeling assumptions and are not verified site-specific estimates.');
  }

  if (finiteGridLine != null && !comparedDirectlyToGrid) {
    if (corrected.length === 0) {
      parts.push(`The electricity price reference benchmark is ${money(finiteGridLine)}.`);
    } else {
      parts.push(
        `The electricity price reference benchmark is ${money(finiteGridLine)} (reported separately without direct comparison to modeled LCOE because a common currency year, real/nominal basis, incentive basis, time/firming basis, service scope, and verified all-in site tariff basis are not established).`,
      );
    }
  }

  if (pendingRows.length > 0) {
    const names = pendingRows.map((r) => r.tech).join(', ');
    parts.push(
      `No verified site-specific LCOE is available yet for ${names} (shown as pending rather than an unverified midpoint or artificial ±8% band). ${composePublishedLcoeReferenceContext()}`,
    );
  }
  if (finiteGridLine != null) {
    parts.push('This reference does not establish an applicable site tariff. Compare the served-load LCOE with the actual all-in tariff, including demand, delivery and time-of-use charges, for the same load and service conditions.');
  }
  return parts.join(' ');
}
